import { useEffect, useState } from 'react';

import { GetStaticProps } from 'next';
import Head from 'next/head';

import dayjs from 'dayjs';

import { getMetadata } from '@/api/reference-data';
import { ContactUs } from '@/components/ContactUs/ContactUs';
import { Footer } from '@/components/Footer/Footer';
import { HeadWithMetaData } from '@/components/HeadWithMetadData';
import {
	FishSearch,
	HeroHeader,
	StartSale,
	WarehouseSearch,
	WarehousesStatistic,
} from '@/components/MainLanding';
import PageScroll from '@/components/PageScroll';
import {
	OPEN_DEMO_WINDOW_TIMER,
	RequestDemoPopup,
	REQUESTED_DEMO,
} from '@/components/RequestDemoPopup/RequestDemoPopup';
import { Metadata } from '@/types/metadata';
import { breadcrumbSchema, graphSchema, organizationSchema } from '@/utils/schema';

type Props = {
	metadata: Metadata;
};

export default function MainLanding({ metadata }: Props) {
	const [isShownRequestDemoPopup, setIsShownRequestDemoPopup] = useState(false);

	useEffect(() => {
		getIsShownRequestDemoPopup();
	}, []);

	const getIsShownRequestDemoPopup = () => {
		const requestDemoPopupCloseTime = localStorage?.getItem(OPEN_DEMO_WINDOW_TIMER);
		const alreadyRequested = localStorage?.getItem(REQUESTED_DEMO);

		if (alreadyRequested) {
			return;
		}

		if (
			!requestDemoPopupCloseTime ||
			dayjs().isAfter(dayjs(requestDemoPopupCloseTime).add(10, 'days'))
		) {
			setTimeout(() => {
				setIsShownRequestDemoPopup(true);
			}, 5000);
		}
	};
	const handleCloseDemoPopup = () => {
		localStorage.setItem(OPEN_DEMO_WINDOW_TIMER, `${dayjs()}`);

		setIsShownRequestDemoPopup(false);
	};

	return (
		<>
			<HeadWithMetaData metadata={metadata} />
			<Head>
				<script
					type="application/ld+json"
					dangerouslySetInnerHTML={{
						__html: JSON.stringify(
							graphSchema([organizationSchema, breadcrumbSchema([{ name: 'Главная', url: '/' }])]),
						),
					}}
				/>
			</Head>
			<PageScroll>
				<PageScroll.Item id="hero-header" mode="dark">
					<HeroHeader />
				</PageScroll.Item>

				<PageScroll.Item id="warehouses-search">
					<WarehouseSearch />
				</PageScroll.Item>

				<PageScroll.Item id="fish-search" mode="dark">
					<FishSearch />
				</PageScroll.Item>

				<PageScroll.Item id="warehouses-statistic">
					<WarehousesStatistic />
				</PageScroll.Item>

				<PageScroll.Item id="start-sale">
					<StartSale />
				</PageScroll.Item>

				<PageScroll.Item id="contact-us" mode="dark">
					<ContactUs />
				</PageScroll.Item>
			</PageScroll>
			<RequestDemoPopup isShownPopup={isShownRequestDemoPopup} onCancel={handleCloseDemoPopup} />
			<Footer />
		</>
	);
}

export const getStaticProps: GetStaticProps = async () => {
	const metadata = await getMetadata('/');

	return {
		props: {
			metadata,
		},
		revalidate: 3600,
	};
};
